// AuthManager.js - Handles Supabase authentication
import { createClient } from '@supabase/supabase-js';

export class AuthManager {
    constructor() {
        this.supabase = null;
        this.currentUser = null;
        this.authStateListeners = [];
        this.initialized = false;
        
        this.initializeSupabase();
    }

    initializeSupabase() {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        if (!supabaseUrl || !supabaseAnonKey) {
            console.warn('⚠️ Supabase credentials not configured. Running in guest-only mode.');
            console.log('Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to .env to enable authentication.');
            this.initialized = false;
            return;
        }

        try {
            this.supabase = createClient(supabaseUrl, supabaseAnonKey);
            this.initialized = true;
            
            // Listen for auth state changes
            this.supabase.auth.onAuthStateChange(async (event, session) => {
                console.log('🔐 Auth state changed:', event);
                
                if (session) {
                    this.currentUser = session.user;
                    
                    // Merge guest usage on sign in
                    if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
                        await this.mergeGuestUsage();
                    }
                } else {
                    this.currentUser = null;
                }
                
                // Notify all listeners
                this.notifyAuthStateChange(event, session);
            });

            // Check for existing session
            this.loadSession();
            
            console.log('✅ Supabase Auth initialized');
        } catch (error) {
            console.error('❌ Failed to initialize Supabase:', error);
            this.initialized = false;
        }
    }

    async loadSession() {
        if (!this.supabase) return;

        try {
            const { data: { session } } = await this.supabase.auth.getSession();
            if (session) {
                this.currentUser = session.user;
                console.log('✅ Existing session loaded:', this.currentUser.email);
            }
        } catch (error) {
            console.error('Failed to load session:', error);
        }
    }

    // Add listener for auth state changes
    onAuthStateChange(callback) {
        this.authStateListeners.push(callback);
        
        // Immediately call with current state
        if (this.currentUser) {
            callback('SIGNED_IN', { user: this.currentUser });
        }
        
        // Return unsubscribe function
        return () => {
            this.authStateListeners = this.authStateListeners.filter(cb => cb !== callback);
        };
    }

    // Notify all listeners of auth state changes
    notifyAuthStateChange(event, session) {
        this.authStateListeners.forEach(callback => {
            callback(event, session);
        });
    }

    // Sign up with email and password
    async signUpWithEmail(email, password) {
        if (!this.supabase) {
            throw new Error('Supabase not initialized. Please configure your Supabase credentials.');
        }

        try {
            const { data, error } = await this.supabase.auth.signUp({
                email,
                password,
            });

            if (error) throw error;

            console.log('✅ Sign up successful:', data.user?.email);
            return { success: true, user: data.user };
        } catch (error) {
            console.error('❌ Sign up failed:', error);
            return { success: false, error: error.message };
        }
    }

    // Sign in with email and password
    async signInWithEmail(email, password) {
        if (!this.supabase) {
            throw new Error('Supabase not initialized. Please configure your Supabase credentials.');
        }

        try {
            const { data, error } = await this.supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) throw error;

            console.log('✅ Sign in successful:', data.user?.email);
            return { success: true, user: data.user };
        } catch (error) {
            console.error('❌ Sign in failed:', error);
            return { success: false, error: error.message };
        }
    }

    // Sign in with OAuth provider (Google, GitHub, etc.)
    async signInWithProvider(provider) {
        if (!this.supabase) {
            throw new Error('Supabase not initialized. Please configure your Supabase credentials.');
        }

        try {
            const { data, error } = await this.supabase.auth.signInWithOAuth({
                provider: provider,
                options: {
                    redirectTo: window.location.origin
                }
            });

            if (error) throw error;

            console.log(`✅ ${provider} OAuth initiated`);
            return { success: true, data };
        } catch (error) {
            console.error(`❌ ${provider} OAuth failed:`, error);
            return { success: false, error: error.message };
        }
    }

    // Sign out
    async signOut() {
        if (!this.supabase) {
            throw new Error('Supabase not initialized.');
        }

        try {
            const { error } = await this.supabase.auth.signOut();
            
            if (error) throw error;

            this.currentUser = null;
            console.log('✅ Sign out successful');
            return { success: true };
        } catch (error) {
            console.error('❌ Sign out failed:', error);
            return { success: false, error: error.message };
        }
    }

    // Get current user
    getUser() {
        return this.currentUser;
    }

    // Check if user is authenticated
    isAuthenticated() {
        return this.currentUser !== null;
    }

    // Get user display name (email or provider name)
    getUserDisplayName() {
        if (!this.currentUser) return null;
        
        // Try to get name from user metadata
        if (this.currentUser.user_metadata?.full_name) {
            return this.currentUser.user_metadata.full_name;
        }
        
        if (this.currentUser.user_metadata?.name) {
            return this.currentUser.user_metadata.name;
        }
        
        // Fall back to email
        return this.currentUser.email;
    }

    // Get user avatar URL
    getUserAvatarUrl() {
        if (!this.currentUser) return null;
        
        // Try to get avatar from user metadata
        if (this.currentUser.user_metadata?.avatar_url) {
            return this.currentUser.user_metadata.avatar_url;
        }
        
        if (this.currentUser.user_metadata?.picture) {
            return this.currentUser.user_metadata.picture;
        }
        
        // Return null if no avatar available
        return null;
    }

    // Check if Supabase is configured
    isConfigured() {
        return this.initialized;
    }
    
    // Merge guest request usage into user account after login
    async mergeGuestUsage() {
        if (!this.currentUser) return;
        
        const sessionId = localStorage.getItem('ai_session_id');
        if (!sessionId) return; // No guest session to merge
        
        try {
            const response = await fetch('/api/automanaged/merge-guest-usage', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: this.currentUser.id,
                    sessionId: sessionId
                })
            });
            
            if (response.ok) {
                const result = await response.json();
                if (result.merged) {
                    console.log(`✅ Merged ${result.guestCount} guest requests. New total: ${result.newTotal}`);
                    
                    // Clear the session ID since it's been merged
                    localStorage.removeItem('ai_session_id');
                }
            }
        } catch (error) {
            console.error('Failed to merge guest usage:', error);
            // Don't throw - this is a nice-to-have feature
        }
    }
}

